/**
 * Stripe Service
 * Handles communication with backend API for Stripe checkout sessions
 */

// Use environment variable or default to Firebase Functions URL
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // Default Firebase Functions URL format
  const projectId = 'connector-40c28';
  const region = 'us-central1';
  if (process.env.NODE_ENV === 'production') {
    return `https://${region}-${projectId}.cloudfunctions.net`;
  }
  return `http://localhost:3000/${projectId}/${region}`;
};

const API_BASE_URL = getApiBaseUrl();

/**
 * Create a checkout session for one-time donation
 * @param {number} amount - Amount in cents (e.g., 500 for $5.00)
 * @param {string} successUrl - URL to redirect after successful payment
 * @param {string} cancelUrl - URL to redirect if payment is cancelled
 * @returns {Promise<string>} Checkout session URL
 */
export const createDonationCheckout = async (amount, successUrl, cancelUrl) => {
  try {
    const response = await fetch(`${API_BASE_URL}/createDonationCheckout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        amount,
        successUrl,
        cancelUrl,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create checkout session');
    }

    const data = await response.json();
    return data.url;
  } catch (error) {
    console.error('Error creating donation checkout:', error);
    throw error;
  }
};

/**
 * Create a checkout session for monthly subscription
 * @param {string} priceId - Stripe Price ID for the subscription
 * @param {string} successUrl - URL to redirect after successful payment
 * @param {string} cancelUrl - URL to redirect if payment is cancelled
 * @returns {Promise<string>} Checkout session URL
 */
export const createSubscriptionCheckout = async (priceId, successUrl, cancelUrl) => {
  try {
    const response = await fetch(`${API_BASE_URL}/createSubscriptionCheckout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        priceId,
        successUrl,
        cancelUrl,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create checkout session');
    }

    const data = await response.json();
    return data.url;
  } catch (error) {
    console.error('Error creating subscription checkout:', error);
    throw error;
  }
};

